<template>
  <BTableSimple show-empty responsive sticky-header>
    <colgroup>
      <col
        v-for="header in tableHeaders"
        :key="header.key"
        :style="header.style"
      />
    </colgroup>

    <BThead>
      <BTr>
        <BTh
          v-for="header in tableHeaders"
          :key="header.key"
          :class="header.class"
        >
          {{ header.label }}
        </BTh>
      </BTr>
    </BThead>

    <BTbody>
      <!-- Empty state -->
      <BTr v-if="!isLoading && items.length === 0" class="b-table-empty-row">
        <td :colspan="nOfTableColumns" class="table-message">
          {{
            isFiltering ? $t('productCategory.NO_RESULTS') : $t('EMPTY_TABLE')
          }}
        </td>
      </BTr>

      <!-- Data rows -->
      <BTr v-for="item in items" :key="`${item.userId}-${item.id}`">
        <BTd>
          <!-- The link sits inside the truncating span so the ellipsis still
               belongs to the cell, not to the anchor. -->
          <span class="full-name-cell">
            <PermittedRouteLink
              :to="{
                name: ROUTE_USER_EDIT,
                params: { userId: String(item.userId) },
              }"
              :data-cy="`attendee-user-${item.userId}`"
            >
              {{ item.fullName }}
            </PermittedRouteLink>
          </span>
        </BTd>

        <!-- Department and position stay plain text: the ILT GraphQL attendee
             exposes both as a bare name with no group id to route to. Tracked
             on FAME-358. -->
        <BTd>
          <span class="department-cell">{{ item.department }}</span>
        </BTd>

        <BTd>
          <span class="position-cell">{{ item.position }}</span>
        </BTd>

        <BTd
          ><NoteInput
            :id="`note-input-${item.id}`"
            :value="item.remarks"
            @onSubmit="(note) => emit('onEditNote', item.id, note)"
          />
        </BTd>

        <BTd>
          <!-- NOTE: An attendee can only be admitted at this time. This will change soon -->
          <BaseSelect
            v-model="item.status"
            aria-label="select user attendance status"
            @change="
              (event) => emit('onStatusChange', item.id, event.target.value)
            "
          >
            <BaseOption :value="RegistrationStatus.Pending" variant="info">
              • {{ $t('product_events.WAITINGLIST') }}
            </BaseOption>
            <BaseOption
              :value="RegistrationStatus.Registered"
              variant="success"
            >
              • {{ $t('product_events.ATTENDING') }}
            </BaseOption>
            <BaseOption
              v-if="
                isDateCompleted || item.status === RegistrationStatus.Attended
              "
              :value="RegistrationStatus.Attended"
              variant="success"
            >
              • {{ $t('product_events.ATTENDED') }}
            </BaseOption>
            <BaseOption :value="RegistrationStatus.NoShow" variant="error">
              • {{ $t('product_events.NO_SHOW') }}
            </BaseOption>
          </BaseSelect>
        </BTd>

        <BTd v-if="isDateCompleted">
          <BaseToggle
            :aria-label="$t('CERTIFY')"
            :disabled="item.certified || !canCertifyUser(item.userId)"
            :checked="item.certified"
            @onChange="emit('onCertify', item.userId, item.id)"
          />
        </BTd>

        <BTd class="actions-cell">
          <BaseButtonIcon
            size="small"
            :title="$t('groups.REMOVE_USER')"
            aria-label="remove user from event"
            outline
            @click="emit('onRemove', item.userId)"
          >
            <RemoveIcon />
          </BaseButtonIcon>
        </BTd>
      </BTr>

      <!-- Skeleton rows: Indicates loading. Should ideally match n of loading items (page size) -->
      <BTr v-for="n in nOfSkeletonRows" :key="n">
        <BTd v-for="i in nOfTableColumns" :key="`${n}${i}`">
          <div class="skeleton-content" />
        </BTd>
      </BTr>

      <!-- Intersection observer row: Tells parent when last item is scrolled into view for fx. initiating fetching more data -->
      <!-- Kept in DOM as long as items exist (not gated on isLoading) so the IntersectionObserver instance is never torn down.
           The parent guards fetchNextPage with !isFetching, so no race condition can occur. -->
      <BTr v-if="canReachEndOfTable">
        <BTd :colspan="nOfTableColumns" class="intersection-observer-cell">
          <IntersectionObserver @onIntersection="emit('onReachEndOfTable')" />
        </BTd>
      </BTr>
    </BTbody>
  </BTableSimple>
</template>

<script setup lang="ts">
import BaseButtonIcon from '@/components/generics/base/BaseButtonIcon.vue'
import BaseSelect, {
  BaseOption,
} from '@/components/generics/base/BaseSelect.vue'
import BaseToggle from '@/components/generics/base/BaseToggle.vue'
import PermittedRouteLink from '@/components/generics/PermittedRouteLink.vue'
import RemoveIcon from '@/components/icon_components/RemoveIcon.vue'
import { IntersectionObserver } from '@/components/utility_components'
// RegistrationStatus is a TS enum, and the template reads it as a value
// (RegistrationStatus.Pending, …). Biome cannot see a .vue <template>, so it
// takes the script's type-only usage as the whole picture; a type import erases
// the binding and blanks out the status select.
// biome-ignore lint/style/useImportType: read as a value in the template
import { RegistrationStatus } from '@/gql/graphql.ts'
import { useI18n } from '@/hooks/useI18N'
import { useSelfCertification } from '@/hooks/users/useSelfCertification'
import { ROUTE_USER_EDIT } from '@/router/routes/routeNames'
import { computed } from 'vue'
import NoteInput from '@/components/product/product_components/component_events/date_info_components/NoteInput.vue'

const i18n = useI18n()
const { canCertifyUser } = useSelfCertification()

const emit = defineEmits<{
  (e: 'onRemove', userId: number): void
  (e: 'onCertify', userId: number, registrationId: number): void
  (e: 'onEditNote', registrationId: number, note: string): void
  (
    e: 'onStatusChange',
    registrationId: number,
    status: RegistrationStatus
  ): void
  (e: 'onReachEndOfTable'): void
}>()

const canReachEndOfTable = computed(() => props.items.length > 0)

export type EventRegistrationTableEntry = {
  id: number
  userId: number
  fullName: string
  department: string
  position: string
  certified: boolean
  status: RegistrationStatus
  remarks: string | null
}

type TableHeader = {
  key: string
  label: string
  style?: Record<string, string | undefined>
  class?: string
}

const props = withDefaults(
  defineProps<{
    isLoading?: boolean
    nOfLoadingItems?: number
    filter?: string
    items: EventRegistrationTableEntry[]
    isDateCompleted: boolean
  }>(),
  {
    isLoading: false,
    nOfLoadingItems: 0,
    filter: undefined,
    items: () => [],
    isDateCompleted: false,
  }
)

const tableHeaders = computed<TableHeader[]>(() => [
  {
    key: 'name',
    label: i18n.t('NAME'),
    style: {
      width: '250px',
      minWidth: '160px',
    },
  },
  {
    key: 'department',
    label: i18n.t('userslist.USERLIST_DEPARTMENT'),
    style: {
      width: '300px',
      minWidth: '90px',
    },
  },
  {
    key: 'positions',
    label: i18n.t('POSITIONS_TAB'),
    style: {
      width: 'auto',
      minWidth: '90px',
    },
  },
  {
    key: 'notes',
    label: i18n.t('NOTES'),
    style: {
      width: '300px',
      minWidth: '200px',
    },
  },
  {
    key: 'status',
    label: i18n.t('ATTENDANCE_STATUS'),
    style: {
      width: '180px',
    },
  },
  ...(props.isDateCompleted
    ? [
        {
          key: 'certified',
          label: i18n.t('CERTIFIED'),
          style: {
            width: '100px',
            minWidth: '80px',
          },
        },
      ]
    : []),
  {
    key: 'actions',
    label: i18n.t('ACTIONS'),
    class: 'align-right',
    style: {
      width: '90px',
      minWidth: '90px',
    },
  },
])

const nOfTableColumns = computed(() => tableHeaders.value.length)
const nOfSkeletonRows = computed(() =>
  props.isLoading ? props.nOfLoadingItems : 0
)
const isFiltering = computed(() => props.filter && props.filter.length > 0)
</script>

<style lang="scss" scoped>
.full-name-cell,
.position-cell,
.department-cell {
  display: inline-block;
  min-width: 0; /* allow it to shrink within the cell */
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.skeleton-content {
  @include skeletonLoadingAnimation;
  height: 32px;
  border-radius: $border-radius-s;
}

.table-message {
  @include subtitle-2;
  height: 100px;
  text-align: center;
  color: $color-text-light;
}

.actions-cell {
  text-align: right;
}

.intersection-observer-cell {
  height: 1px;
  padding: 0;
  visibility: hidden;
}

.attendee-comment {
  height: auto;
  resize: none;
  width: 100%;
}
</style>
